import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  fetchTask,
  CheckIfTimeSessionStartOrNot,
  UpdateTimeSession,
  GetTimeSession,
  GetActiveTimeSessionOnPlanboard,
  CreateNewTimeSession,
} from "../../redux/actions/action";
import {
  CHECK_IF_TIME_SESSION,
  UPDATE_TIME_SESSION,
} from "../../redux/constants/type";
import clipboardTrack from "../../assets/icons/clipboard_track.png";
import timerImage from "../../assets/icons/timer-image.png";
import playButton from "../../assets/icons/Play.svg";
import checkedButton from "../../assets/icons/WhiteChecked.svg";
import stopButton from "../../assets/icons/blue-stop-circle.svg";
import "../../assets/styles/local/MvpNewTrack.css";

// Custom hook for timer management
const useTimer = (isActive) => {
  const GetTimeSessionResponse = useSelector(
    (state) => state.user.GetTimeSessionResponse,
  );

  // initial value 0
  const [timer, setTimer] = useState(0);

  // ✅ Update timer when API response comes
  useEffect(() => {
    if (GetTimeSessionResponse.status === true) {
      const totalTrackedTime =
        GetTimeSessionResponse?.data?.currentTimeSession?.totalTrackedTime || 0;
      const initialTrackedSeconds = totalTrackedTime * 3600;

      setTimer(initialTrackedSeconds);
    }
  }, [GetTimeSessionResponse]);

  // ✅ increment timer every second
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]);

  const resetTimer = () => setTimer(0);

  return { timer, resetTimer };
};

// Utility function to format timer
const formatTimer = (seconds) => {
  const totalSeconds = Math.floor(seconds); // ✅ round down to nearest integer
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    "0",
  );
  const secs = String(totalSeconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${secs}`;
};

const MvpNewTrack = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { taskId } = useParams();

  // State variables
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [totalHours, setTotalHours] = useState("00");
  const [totalMinutes, setTotalMinutes] = useState("00");

  const userid = localStorage.getItem("userid");

  // Timer logic
  const { timer, resetTimer } = useTimer(isToggleOn);

  // Redux selectors
  const {
    fetchTaskResponse,
    CheckIfTimeSessionStartOrNotResponse,
    GetTimeSessionResponse,
    CreateNewTimeSessionResponse,
    UpdateTimeSessionResponse,
  } = useSelector((state) => state.user);

  // Effects
  useEffect(() => {
    dispatch(fetchTask(taskId));
    dispatch(
      GetTimeSession(
        "00000000-0000-0000-0000-000000000000",
        taskId,
        userid,
        moment().format("DD/MM/YYYY"),
        moment().format("HH:mm:ss"),
      ),
    );
  }, [dispatch, taskId, userid]);

  useEffect(() => {
    if (GetTimeSessionResponse) {
      const totalTime = GetTimeSessionResponse?.data?.totalHoursTracked || 0;
      const [hours, minutes] = totalTime.toString().split(".");
      setTotalHours(hours?.padStart(2, "0"));
      setTotalMinutes(minutes?.padStart(2, "0"));
    }
  }, [GetTimeSessionResponse]);

  useEffect(() => {
    if (GetTimeSessionResponse.status === true) {
      setIsToggleOn(true);
    } else {
      setIsToggleOn(false);
    }
  }, [GetTimeSessionResponse]);

  useEffect(() => {
    if (fetchTaskResponse?.status) {
      setTaskData(fetchTaskResponse?.data[0]);
    }
  }, [fetchTaskResponse]);

  useEffect(() => {
    if (CreateNewTimeSessionResponse?.status) {
      setIsToggleOn(true);
      localStorage.setItem(
        "trackingTaskId",
        CreateNewTimeSessionResponse?.data?.projectTaskTrackingTimeid,
      );
      dispatch(
        GetActiveTimeSessionOnPlanboard(
          userid,
          moment().format("DD/MM/YYYY"),
          moment().format("HH:mm:ss"),
        ),
      );
    }
  }, [CreateNewTimeSessionResponse, dispatch, userid]);

  useEffect(() => {
    if (CheckIfTimeSessionStartOrNotResponse?.status === true) {
      dispatch(
        CreateNewTimeSession(
          "00000000-0000-0000-0000-000000000000",
          taskId,
          moment().format("DD/MM/YYYY"),
          moment().format("HH:mm:ss"),
          "Work",
          "",
          0,
          userid,
        ),
      );
      setTimeout(() => {
        dispatch({
          type: CHECK_IF_TIME_SESSION,
          data: {},
        });
      }, 2000);
    }
  }, [CheckIfTimeSessionStartOrNotResponse, dispatch, taskId, userid]);

  useEffect(() => {
    if (UpdateTimeSessionResponse?.status === true) {
      setIsTimerRunning(false);
      setIsToggleOn(false);
      localStorage.setItem("timerRunning", false);
      localStorage.removeItem("timerTask");
      resetTimer();
      dispatch(
        GetTimeSession(
          "00000000-0000-0000-0000-000000000000",
          taskId,
          userid,
          // "22:28:13",
          moment(new Date()).format("DD/MM/YYYY"),
          moment().format("HH:mm:ss"),
        ),
      );
      localStorage.removeItem("trackingTaskId");

      setTimeout(() => {
        dispatch({
          type: UPDATE_TIME_SESSION,
          data: {},
        });
      }, 2000);
    }
  }, [UpdateTimeSessionResponse]);

  useEffect(() => {
    if (GetTimeSessionResponse !== null) {
      const totalTrackedTime = 0.020277778; // example value from API
      const timeInSeconds = Math.round(totalTrackedTime * 3600);
      // Call formatTimer with converted seconds
      // setTimer(timeInSeconds);

      // For Total Timer in Below.
      let ascTotalTime = GetTimeSessionResponse?.data?.totalHoursTracked;

      setTotalHours(ascTotalTime?.toString().split(".")[0]?.padStart(2, "0"));
      setTotalMinutes(ascTotalTime?.toString().split(".")[1]?.padStart(2, "0"));
    }
  }, [GetTimeSessionResponse]);

  // Handlers
  const handleCancel = () => history.goBack();

  const handleToggle = () => {
    const newState = !isToggleOn;
    if (newState) {
      startTimer();
    } else {
      stopTimer();
    }
    setIsToggleOn(newState);
    setIsTimerRunning(newState);
    if (!newState) resetTimer();
  };

  const startTimer = () => {
    dispatch(
      CheckIfTimeSessionStartOrNot(
        "00000000-0000-0000-0000-000000000000",
        taskId,
        userid,
        moment().format("DD/MM/YYYY"),
        moment().format("HH:mm:ss"),
      ),
    );
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    setIsToggleOn(false);
    const trackingTaskId = localStorage.getItem("trackingTaskId");
    dispatch(
      UpdateTimeSession(
        trackingTaskId,
        moment().format("DD/MM/YYYY"),
        moment().format("HH:mm:ss"),
      ),
    );
  };

  return (
    <>
      <div className="mvp-new-track d-none">
        <div className="card-task">
          <div className="header">
            <img src={clipboardTrack} alt="icon" className="button-icon" />
            <span className="title">Task</span>
          </div>
          <div className="task-info">
            <span className="task-title">{taskData?.taskname}</span>
            <input
              className="task-input"
              value={
                taskData
                  ? `${taskData.project[0]?.projectcode} | ${taskData.project[0]?.projectname}`
                  : ""
              }
              readOnly
            />
            <input
              className="company-input"
              value={taskData?.contact[0]?.contactname || ""}
              readOnly
            />
          </div>
          <div className="description">
            <p className="description-title">Task Description</p>
            <a href="#" className="description-link">
              {taskData?.taskdescription || ""}
            </a>
          </div>
          <div className="toggle-track" onClick={handleToggle}>
            <div className={`toggle-switch ${isToggleOn ? "active" : ""}`}>
              {isToggleOn && <span className="toggle-label">Recording</span>}
              <div className="toggle-circle">
                <span>{isToggleOn ? "ON" : "OFF"}</span>
              </div>
            </div>
          </div>
          <div className="timer">
            <div
              className="timer-circle"
              style={{ backgroundImage: `url(${timerImage})` }}
            >
              <span className="timer-time">{formatTimer(timer)}</span>
              <span className="timer-text">Current Session</span>
            </div>
          </div>
          <div className="time-track-total">
            <div className="time-info">
              <span className="time-value">{`${totalHours}:${totalMinutes} hrs`}</span>
              <span className="time-label">Today, Wednesday</span>
            </div>
            <div className="time-info">
              <span className="time-value">{`${totalHours}:${totalMinutes} hrs`}</span>
              <span className="time-label">Total</span>
            </div>
          </div>
        </div>
        <div className="bottom-btn-div d-none">
          {!isTimerRunning ? (
            <button className="cancel-btn2" onClick={handleCancel}>
              Cancel
            </button>
          ) : (
            <button className="stop-btn" onClick={stopTimer}>
              Stop
            </button>
          )}
        </div>
      </div>

      {/* ASC New 👇 */}
      <div>
        <div className="toggle-track d-none" onClick={handleToggle}>
          <div className={`toggle-switch ${isToggleOn ? "active" : ""}`}>
            {isToggleOn && <span className="toggle-label">Recording</span>}
            <div className="toggle-circle">
              <span>{isToggleOn ? "ON" : "OFF"}</span>
            </div>
          </div>
        </div>

        {isToggleOn ? (
          <div
            className="p-2"
            style={{
              position: "fixed",
              bottom: "100px",
              right: "20px",
              background: "#D2DCE9",
              borderRadius: "8px",
              minWidth: "150px",
            }}
          >
            <div
              className="newtracker"
              // style={{ backgroundImage: `url(${timerImage})` }}
            >
              <span className="timer-time">Session</span>
              <span className="timer-time">{formatTimer(timer)}</span>
            </div>

            <div
              style={{ borderBottom: "2px solid white", padding: "5px" }}
            ></div>

            {/* <button
              className="navigation-button navigation-button-track pt-2"
              disabled={isToggleOn}
              style={{
                opacity: isToggleOn ? "0.5" : "1",
                cursor: isToggleOn ? "default" : "",
                background: "#D2DCE9",
                color: "#00246a",
              }}
              onClick={handleToggle}
            >
              <img src={playButton} alt="" />
              <h6 className="mb-0">Pause</h6>
            </button> */}

            <button
              className="navigation-button2 navigation-button-track pt-2"
              disabled={!isToggleOn}
              style={{
                opacity: !isToggleOn ? "0.5" : "1",
                cursor: !isToggleOn ? "default" : "",
                background: "#D2DCE9",
                color: "#00246a",
                marginLeft: "35px",
              }}
              onClick={handleToggle}
            >
              <img src={stopButton} alt="" />
              <h6 className="mb-0">Stop</h6>
            </button>
          </div>
        ) : (
          <div
            className="p-2"
            style={{
              position: "fixed",
              bottom: "80px",
              right: "15px",
              background: "#00246a",
              borderRadius: "8px",
            }}
          >
            <button
              className="navigation-button navigation-button-track"
              disabled={isToggleOn}
              style={{
                opacity: isToggleOn ? "0.5" : "1",
                cursor: isToggleOn ? "default" : "",
              }}
              onClick={handleToggle}
            >
              <img src={playButton} alt="" />
              <h6 className="mb-0">Start</h6>
            </button>

            {/* <button
              className="navigation-button2 navigation-button-track"
              disabled={!isToggleOn}
              style={{
                opacity: !isToggleOn ? "0.5" : "1",
                cursor: !isToggleOn ? "default" : "",
              }}
              onClick={handleToggle}
            >
              <img src={checkedButton} alt="" />
              <h6 className="mb-0">Complete</h6>
            </button> */}

            <button
              className="navigation-button2 navigation-button-track"
              // disabled={!isToggleOn}
              disabled={props?.pdfLoading}
              style={
                {
                  // opacity: !isToggleOn ? "0.5" : "1",
                  // cursor: !isToggleOn ? "default" : "",
                }
              }
              onClick={() => {
                // handleToggle();
                props.childData(true);
              }}
            >
              <img src={checkedButton} alt="" />
              {/* <h6 className="mb-0">Complete</h6> */}
              <h6 className="mb-0">
                {props?.pdfLoading ? "Please wait..." : "Complete"}
              </h6>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default MvpNewTrack;
